import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import {
  CalendarEventTimesChangedEvent,
  CalendarMonthViewDay,
} from "angular-calendar";
import dayjs from "dayjs";
import cloneDeep from "lodash/cloneDeep";
import { finalize, mergeMap, Subject, Subscription, tap } from "rxjs";
import { IBrandContentCalendarPostEvent } from "../../../shared/interfaces/IBrandContentCalendarPostEvent";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import { BrandCampaignCalendar } from "../../../shared/models/brandCampaignCalendar";
import {
  BrandCalendarPost,
  BrandCampaignCalendarPost,
  BrandCampaignFacebookCalendarPost,
  BrandCampaignGoogleCalendarPost,
  BrandCampaignLinkedInCalendarPost,
} from "../../../shared/models/brandCampaignCalendarPost";
import { BrandCampaignDetails } from "../../../shared/models/brandCampaignDetail";
import { BrandCampaignCalendarService } from "../../../shared/services/api/brand-campaign-calendar.service";
import { BrandService } from "../../../shared/services/api/brand.service";
import { DateService } from "../../../shared/services/date.service";
import { getLanguageSignal } from "../../../shared/services/language.service";
import { NotificationService } from "../../../shared/services/notification.service";
import { BrandCalendarPostService } from "../shared/services/brand-calendar-post.service";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";
import { BrandCampaignContentCalendarPostEditDialogData } from "./brand-campaign-content-calendar-post-edit-dialog/brand-campaign-content-calendar-post-edit-dialog-data";
import { BrandCampaignContentCalendarPostEditDialogComponent } from "./brand-campaign-content-calendar-post-edit-dialog/brand-campaign-content-calendar-post-edit-dialog.component";
import { BrandCampaignContentCalendarPostSelectionDialogData } from "./brand-campaign-content-calendar-post-selection-dialog/brand-campaign-content-calendar-post-selection-dialog-data";
import { BrandCampaignContentCalendarPostSelectionDialogComponent } from "./brand-campaign-content-calendar-post-selection-dialog/brand-campaign-content-calendar-post-selection-dialog.component";

@Component({
  selector: "app-brand-campaign-content-calendar",
  templateUrl: "./brand-campaign-content-calendar.component.html",
  styleUrl: "./brand-campaign-content-calendar.component.scss",
  providers: [BrandCampaignCalendarService],
})
export class BrandCampaignContentCalendarComponent
  implements OnInit, OnDestroy
{
  protected readonly refresh = new Subject<void>();
  protected readonly VIEW_TYPE = "month";
  protected events: IBrandContentCalendarPostEvent[] = [];
  protected language = getLanguageSignal();
  protected loading = false;
  protected viewDate: Date;

  private readonly subscriptions = new Subscription();
  private campaign: BrandCampaign;
  private campaignDetails: BrandCampaignDetails;
  private campaignEndDate: Date;
  private campaignStartDate: Date;
  private currentCalendar: BrandCampaignCalendar;
  private daysFromStartToDrag?: number;
  private fakeEvent?: IBrandContentCalendarPostEvent;

  constructor(
    private readonly brandCalendarPostService: BrandCalendarPostService,
    private readonly brandCampaignCalendarService: BrandCampaignCalendarService,
    private readonly brandCampaignService: BrandCampaignService,
    private readonly brandService: BrandService,
    private readonly dateService: DateService,
    private readonly dialog: MatDialog,
    private readonly notificationService: NotificationService,
  ) {
    this.campaign = this.brandCampaignService.currentBrandCampaign;
    this.campaignDetails = this.campaign.currentDetails;
    this.currentCalendar = this.campaign.currentCalendar;
    this.campaignStartDate = dayjs(this.campaign.startDate)
      .startOf("day")
      .toDate();
    this.viewDate = this.campaignStartDate;
    this.campaignEndDate = dayjs(this.campaign.endDate).endOf("day").toDate();
    this.events = [];

    if (this.currentCalendar) {
      this.currentCalendar.calendarPosts.forEach((post) => {
        this.processPost(post as BrandCalendarPost);
      });
    }

    this.setEventsPositions();
  }

  public ngOnInit(): void {
    window.scrollTo(0, 0);
    this.refresh.next();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private addPost(calendarPost: BrandCalendarPost): void {
    this.currentCalendar.calendarPosts.push(calendarPost);
    this.processPost(calendarPost);
  }

  private processPost(post: BrandCalendarPost): void {
    const editable = this.campaign.isEditable;
    const draggable = this.campaign.isEditable;
    let title = "";

    if (post.isFacebookPost) {
      const facebookPost = post as BrandCampaignFacebookCalendarPost;
      title = facebookPost.text;
    } else if (post.isGoogleAd) {
      const googlePost = post as BrandCampaignGoogleCalendarPost;
      title = googlePost.descriptionOne;
    } else if (post.isLinkedInPost) {
      const linkedInPost = post as BrandCampaignLinkedInCalendarPost;
      title = linkedInPost.text;
    }

    const newPost = {
      id: post.calendarPostId,
      title: title,
      start: post.suggestedPublishDate,
      post: post,
      editable: editable,
      draggable: draggable,
      blocked: false, // Flag used to refresh this event's post unless it's blocked by the frontend
    };

    this.events.push(newPost);
    this.refresh.next();
  }

  protected isEventLoading(event: IBrandContentCalendarPostEvent): boolean {
    return event.loading ?? false;
  }

  protected eventTimesChanged({
    event,
    newStart,
  }: CalendarEventTimesChangedEvent): void {
    if (this.isDragInProgress()) {
      return;
    }

    const eventChanged = event as IBrandContentCalendarPostEvent;
    // Save old value in case the transaction fails
    const oldStart = new Date(event.start);
    const oldDate = dayjs(eventChanged.post!.suggestedPublishDate);
    const newDate = dayjs(
      newStart.setDate(newStart.getDate() - this.daysFromStartToDrag!),
    );

    if (oldDate.format("MM-DD-YYYY") === newDate.format("MM-DD-YYYY")) {
      eventChanged.loading = false;
      eventChanged.blocked = false;
      return; // If is dragged on the same day we do not update it
    }

    // Update the variable in memory and the calendar's UI to show immediate feedback to user
    eventChanged.loading = true;
    eventChanged.blocked = true;

    eventChanged.start = newDate.toDate();
    eventChanged.end = this.getEndDate(eventChanged);

    this.refresh.next();

    if (this.isDayOutsideRange(newStart)) {
      this.notificationService.error(
        "brand.campaign.contentCalendar.dateOutOfRange",
        "brand.campaign.contentCalendar.errorUpdatingThePost",
      );
      this.rollBackEvent(eventChanged, oldStart);
    } else if (newStart < new Date()) {
      this.notificationService.error(
        "brand.campaign.contentCalendar.theNewDateCanNotBeInThePast",
        "brand.campaign.contentCalendar.errorUpdatingThePost",
      );
      this.rollBackEvent(eventChanged, oldStart);
    } else {
      newDate.minute(oldDate.minute());
      newDate.hour(oldDate.hour());
      this.brandCalendarPostService
        .updateBrandCalendarPost(
          this.campaign.id,
          this.currentCalendar.id,
          eventChanged.post!.calendarPostId,
          { suggested_publish_date: newDate.unix() },
        )
        .subscribe({
          next: () => {
            // Update post's date value
            eventChanged.start = newDate.toDate();
            eventChanged.post!.suggestedPublishDate = newDate.toDate();
            eventChanged.end = this.getEndDate(eventChanged);
            eventChanged.loading = false;
            eventChanged.blocked = false;
            this.resetEventPosition();
            this.notificationService.success(
              "brand.campaign.contentCalendar.scheduleTimeUpdated",
              "brand.campaign.contentCalendar.postUpdated",
            );
          },
          error: () => {
            // Undo the change, refresh the UI and show error message to user
            eventChanged.start = oldStart;
            eventChanged.end = this.getEndDate(eventChanged);
            eventChanged.loading = false;
            eventChanged.blocked = false;
            this.refresh.next();
            this.notificationService.error(
              "shared.changesCouldNotBeSaved",
              "brand.campaign.contentCalendar.errorUpdatingThePost",
            );
          },
        });
    }
  }

  private rollBackEvent(
    event: IBrandContentCalendarPostEvent,
    oldStart: Date,
  ): void {
    event.start = oldStart;
    event.loading = false;
    this.refresh.next();
  }

  protected onEventClick(
    mouseEvent: MouseEvent,
    event: IBrandContentCalendarPostEvent,
  ): void {
    if (mouseEvent.stopPropagation) {
      mouseEvent.stopPropagation();
    }

    const dialogRef = this.dialog.open(
      BrandCampaignContentCalendarPostEditDialogComponent,
      {
        width: "530px",
        data: new BrandCampaignContentCalendarPostEditDialogData(
          event.post!,
          this.brandService.currentBrandId,
          this.campaign,
          this.campaignDetails,
          this.currentCalendar,
        ),
        panelClass: "no-padding-dialog",
        disableClose: true,
      },
    );

    dialogRef.componentInstance.postEdited
      .pipe(
        tap((calendarPost) => {
          const post = calendarPost as BrandCalendarPost;

          event.start = post.suggestedPublishDate;
          event.post = post;
        }),
        mergeMap(() =>
          this.brandCampaignService.loadBrandCampaign(this.campaign.id),
        ),
        finalize(() => {
          this.refresh.next();
          this.notificationService.success(
            "brand.campaign.contentCalendar.postUpdated",
          );
        }),
      )
      .subscribe();

    dialogRef.componentInstance.postRemoved.subscribe((calendarPost) => {
      this.currentCalendar.calendarPosts.forEach((loopCalendarPost, idx) => {
        if (loopCalendarPost.calendarPostId === calendarPost.calendarPostId) {
          this.currentCalendar.calendarPosts.splice(idx, 1);
        }
      });

      this.events = this.events.filter(
        (post) => post.id !== calendarPost.calendarPostId,
      );

      this.refreshCampaign();
      this.notificationService.success(
        "brand.campaign.contentCalendar.postUpdated",
      );
    });
  }

  private async refreshCampaign(): Promise<void> {
    this.campaign = (await this.brandCampaignService.loadBrandCampaign(
      this.campaign.id,
    )) as BrandCampaign;

    this.refresh.next();
  }

  protected isCalendarOnThisMonth(): boolean {
    const now = new Date();

    return (
      now.getMonth() === this.viewDate.getMonth() &&
      now.getFullYear() === this.viewDate.getFullYear()
    );
  }

  protected isDayOutsideRange(date: Date): boolean {
    return this.dateService.isDateOutsideRange(date, {
      startDate: this.campaignStartDate,
      endDate: this.campaignEndDate,
    });
  }

  protected checkAndShowSelectPostDialog(day: CalendarMonthViewDay): void {
    if (!this.campaign.currentCalendar) {
      const calendar = new BrandCampaignCalendar({ title: "" });

      this.brandCampaignCalendarService
        .createBrandCalendar(
          this.brandService.currentBrandId,
          this.campaign.id,
          this.campaign.currentDetails.id,
          calendar,
        )
        .subscribe({
          next: (calendarCreated) => {
            this.campaign.currentDetails.calendars.push(calendarCreated);
            this.currentCalendar = calendarCreated;
            this.showSelectPostDialog(day);
          },
          error: () =>
            this.notificationService.error(
              "brand.campaign.contentCalendar.missingCalendar",
            ),
        });
      return;
    }

    this.showSelectPostDialog(day);
  }

  private showSelectPostDialog(day: CalendarMonthViewDay): void {
    const campaignDetailsWithPosts =
      this.campaign.currentDetails.categories.filter(
        ({ posts }) => posts.length > 0,
      );

    if (campaignDetailsWithPosts.length === 0) {
      this.notificationService.error(
        "brand.campaign.contentCalendar.addSomePostsFirst",
      );
      return;
    }

    this.dialog
      .open(BrandCampaignContentCalendarPostSelectionDialogComponent, {
        data: new BrandCampaignContentCalendarPostSelectionDialogData(
          this.brandService.currentBrandId,
          this.campaign,
          this.campaignDetails,
          this.currentCalendar,
          day.date,
        ),
        panelClass: "no-padding-dialog",
        disableClose: true,
      })
      .afterClosed()
      .subscribe((calendarPost: BrandCalendarPost) => {
        if (calendarPost) {
          this.addPost(calendarPost);
          this.resetEventPosition();
          this.notificationService.success(
            "brand.campaign.contentCalendar.postCreated",
          );
        }
      });
  }

  protected showAddPostButton(day: CalendarMonthViewDay): boolean {
    if (!this.campaign) {
      return false;
    } else if (!this.campaign.isEditable) {
      return false;
    } else if (!this.campaignStartDate || !this.campaignEndDate) {
      return false;
    } else if (day.date < this.campaignStartDate) {
      return false;
    } else if (day.date > this.campaignEndDate) {
      return false;
    }

    return !this.isDragInProgress();
  }

  protected onEventDragStart(
    event: IBrandContentCalendarPostEvent,
    day: Date,
  ): void {
    this.daysFromStartToDrag = this.getDaysBetween(event.start, day);
    this.fakeEvent = cloneDeep(event);
    this.fakeEvent.isFake = true;
    this.events.push(this.fakeEvent);
    event.isBeingDragged = true;
  }

  protected onMouseUp(): void {
    if (this.isDragInProgress()) {
      this.fakeEvent = undefined;
      this.events.pop();
      this.events.forEach((e) => (e.isBeingDragged = false));
      this.resetEventPosition();
    }
  }

  protected mouseOverDay(date: Date): void {
    if (this.fakeEvent) {
      // dragging
      for (const event of this.events) {
        if (event.isFake) {
          this.setFakeDayWhileDrag(event, date);
        }
      }
      this.resetEventPosition();
    }
  }

  private setFakeDayWhileDrag(
    event: IBrandContentCalendarPostEvent,
    date: Date,
  ): void {
    event.dragStartDate = new Date(date);
    event.dragStartDate.setDate(date.getDate() - this.daysFromStartToDrag!);
    event.dragEndDate = this.getEndDate(event);
  }

  // SET EVENTS POSITIONS

  protected getFilledEventArray(date: Date): IBrandContentCalendarPostEvent[] {
    const filledArray: IBrandContentCalendarPostEvent[] = [];
    const events = this.getDayEvents(date);

    let maxPosition = -1;

    for (const event of events) {
      if (event.position! > maxPosition) {
        maxPosition = event.position!;
      }
    }

    for (
      let currentPosition = 0;
      currentPosition <= maxPosition;
      currentPosition++
    ) {
      const currentEvent = events.find(
        (event) => event.position === currentPosition,
      );

      if (currentEvent) {
        filledArray.push(currentEvent);
      } else {
        filledArray.push({} as IBrandContentCalendarPostEvent);
      }
    }

    return filledArray;
  }

  private resetEventPosition(): void {
    this.events.forEach((event) => (event.position = undefined));
    this.setEventsPositions();
  }

  private setEventsPositions(): void {
    const [firstDay, lastDay] = this.findEventsRange();

    for (
      let day = new Date(firstDay ?? this.campaignStartDate);
      this.getComparableDateString(day) <=
      this.getComparableDateString(lastDay ?? this.campaignEndDate);
      day.setDate(day.getDate() + 1)
    ) {
      const dayEvents = this.getDayEvents(day);

      for (const event of dayEvents) {
        if (event.isBeingDragged) {
          event.position = dayEvents.length - 1;
        }

        if (event.position === undefined) {
          event.position = this.setFirstFreePosition(dayEvents);
        }
      }
    }
  }

  private setFirstFreePosition(
    dayEvents: IBrandContentCalendarPostEvent[],
  ): number {
    let currentPosition = 0;
    let found = true;
    while (found) {
      found = false;
      for (const e of dayEvents) {
        if (e.position === currentPosition) {
          found = true;
          break;
        }
      }
      if (found) {
        currentPosition++;
      }
    }
    return currentPosition;
  }

  private getDayEvents(currentDay: Date): IBrandContentCalendarPostEvent[] {
    const events = this.events ?? [];
    return events.filter((event) => {
      const eventStart = this.startDay(event);
      const eventEnd = this.endDay(event);

      if (!eventEnd) {
        return (
          this.getComparableDateString(eventStart) ===
          this.getComparableDateString(currentDay)
        );
      }

      // We only keep the event from the dragged day
      const dayDragged = new Date(event.start);
      dayDragged.setDate(dayDragged.getDate() + this.daysFromStartToDrag!);

      if (
        event.isBeingDragged &&
        !event.isFake &&
        this.getComparableDateString(currentDay) !==
          this.getComparableDateString(dayDragged)
      ) {
        return false;
      }

      for (
        let day = new Date(eventStart);
        this.getComparableDateString(day) <=
        this.getComparableDateString(eventEnd);
        day.setDate(day.getDate() + 1)
      ) {
        if (
          this.getComparableDateString(day) ===
          this.getComparableDateString(currentDay)
        ) {
          return true;
        }
      }
      return false;
    });
  }

  private findEventsRange(): [Date | undefined, Date | undefined] {
    let minDate: Date | undefined;
    let maxDate: Date | undefined;

    for (const event of this.events) {
      const eventStart = this.startDay(event);
      const eventEnd = this.endDay(event);

      if (!minDate) {
        minDate = eventStart;
      } else if (minDate > eventStart) {
        minDate = eventStart;
      }

      if (!maxDate) {
        maxDate = eventEnd ? eventEnd : eventStart;
      } else if (eventEnd) {
        if (eventEnd > maxDate) {
          maxDate = eventEnd;
        }
      } else if (eventStart > maxDate) {
        maxDate = eventStart;
      }
    }

    return [minDate, maxDate];
  }

  private getComparableDateString(date: Date): string {
    return dayjs(date).format("YYYYMMDD");
  }

  protected isEmptyObject(object: IBrandContentCalendarPostEvent): boolean {
    return Object.keys(object).length === 0;
  }

  protected isFirstDay(
    day: Date,
    event: IBrandContentCalendarPostEvent,
  ): boolean {
    return this.endDay(event)
      ? this.getComparableDateString(day) ===
          this.getComparableDateString(this.startDay(event))
      : false;
  }

  protected isCenterDay(
    day: Date,
    event: IBrandContentCalendarPostEvent,
  ): boolean {
    return (
      !this.isOneDayEvent(event) &&
      !this.isFirstDay(day, event) &&
      !this.isLastDay(day, event)
    );
  }

  protected isLastDay(
    day: Date,
    event: IBrandContentCalendarPostEvent,
  ): boolean {
    return this.endDay(event)
      ? this.getComparableDateString(day) ===
          this.getComparableDateString(this.endDay(event))
      : false;
  }

  protected isOneDayEvent(event: IBrandContentCalendarPostEvent): boolean {
    return (
      !this.endDay(event) ||
      this.getComparableDateString(this.startDay(event)) ===
        this.getComparableDateString(this.endDay(event))
    );
  }

  private getDaysBetween(date1: Date, date2: Date): number {
    return Math.ceil((date2.valueOf() - date1.valueOf()) / (1000 * 3600 * 24));
  }

  private startDay(event: IBrandContentCalendarPostEvent): Date {
    return event.dragStartDate ? event.dragStartDate : event.start;
  }

  private endDay(event: IBrandContentCalendarPostEvent): Date {
    return event.dragEndDate ? event.dragEndDate : this.getEndDate(event);
  }

  private isDragInProgress(): boolean {
    return !!this.fakeEvent;
  }

  protected isMultiDayPost(post: BrandCampaignCalendarPost): boolean {
    if (!post) {
      return false;
    } else if (post.isGoogleAd) {
      return true;
    } else if (post.isFacebookPost && post.willBecomeAd) {
      return (this.campaign.adDuration ?? 0) > 0;
    }

    return false;
  }

  private getEndDate(event: IBrandContentCalendarPostEvent): Date {
    const isAd =
      (event.post!.isFacebookPost && event.post!.willBecomeAd) ||
      event.post!.isGoogleAd;

    if (isAd) {
      if (this.campaign.adDuration) {
        const fullLengthEndDate = new Date(
          new Date(this.startDay(event)).getTime() +
            this.campaign.adDuration * 24 * 60 * 60 * 1000,
        );
        const campaignEndDate = this.campaign.endDate;

        if (
          this.getComparableDateString(fullLengthEndDate) <
          this.getComparableDateString(campaignEndDate)
        ) {
          return fullLengthEndDate;
        }
        return this.campaign.endDate;
      }
    }

    return this.startDay(event);
  }

  protected showFacebookIcon(post: BrandCampaignCalendarPost): boolean {
    return (
      post instanceof BrandCampaignFacebookCalendarPost &&
      (post.postToFacebook ||
        (post.willBecomeAd && this.campaign.publisherPlatformFacebook))
    );
  }

  protected showInstagramIcon(post: BrandCampaignCalendarPost): boolean {
    return (
      post instanceof BrandCampaignFacebookCalendarPost &&
      (post.postToInstagram ||
        (post.willBecomeAd && this.campaign.publisherPlatformInstagram))
    );
  }
}
